import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  getLeadFavorites,
  getLeadFavoriteIDs,
  addLeadFavorite,
  removeLeadFavorite,
  makeLeadFavoriteKey,
} from "../services/LeadsFavoritesService";
import { useUserContext } from "../context/UserContext";

export const leadsFavoritesKeys = {
  all: ["leads-favorites"] as const,
  list: (retailer?: string) => [...leadsFavoritesKeys.all, "list", retailer] as const,
  ids: (retailer?: string) => [...leadsFavoritesKeys.all, "ids", retailer] as const,
};

// Hook to get all saved leads with details
export const useLeadFavorites = (retailer?: string) => {
  const { user } = useUserContext();
  return useQuery({
    queryKey: leadsFavoritesKeys.list(retailer),
    queryFn: () => getLeadFavorites(retailer),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get just the IDs (lightweight, for checking if saved)
// Returns composite keys: "retailer#leadId"
export const useLeadFavoriteIDs = (retailer?: string) => {
  const { user } = useUserContext();
  return useQuery({
    queryKey: leadsFavoritesKeys.ids(retailer),
    queryFn: () => getLeadFavoriteIDs(retailer),
    enabled: !!user && !!retailer,
    staleTime: 30 * 1000, // Shorter stale time for fresher data
    refetchOnMount: "always",
    placeholderData: (prev) => prev ?? [], // Use previous data or empty array
  });
};

// Hook to add a lead to favorites
export const useAddLeadFavorite = (retailer?: string, showToast = true) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, retailer: r }: { leadId: string; retailer: string }) =>
      addLeadFavorite(leadId, r),
    onMutate: async ({ leadId, retailer: r }) => {
      const compositeKey = makeLeadFavoriteKey(r, leadId);
      await queryClient.cancelQueries({ queryKey: leadsFavoritesKeys.ids(retailer) });
      const prev = queryClient.getQueryData<string[]>(leadsFavoritesKeys.ids(retailer));
      queryClient.setQueryData<string[]>(leadsFavoritesKeys.ids(retailer), (old) =>
        old ? [...old, compositeKey] : [compositeKey]
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(leadsFavoritesKeys.ids(retailer), ctx.prev);
      toast.error("Failed to save lead");
    },
    onSuccess: () => {
      if (showToast) toast.success("Lead saved");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: leadsFavoritesKeys.all });
    },
  });
};

// Hook to remove a lead from favorites (expects composite key: retailer#leadId)
export const useRemoveLeadFavorite = (retailer?: string, showToast = true) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (compositeKey: string) => removeLeadFavorite(compositeKey),
    onMutate: async (compositeKey) => {
      await queryClient.cancelQueries({ queryKey: leadsFavoritesKeys.ids(retailer) });
      const prev = queryClient.getQueryData<string[]>(leadsFavoritesKeys.ids(retailer));
      queryClient.setQueryData<string[]>(leadsFavoritesKeys.ids(retailer), (old) =>
        old ? old.filter((id) => id !== compositeKey) : []
      );
      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(leadsFavoritesKeys.ids(retailer), ctx.prev);
      toast.error("Failed to remove lead");
    },
    onSuccess: () => {
      if (showToast) toast.success("Lead removed");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: leadsFavoritesKeys.all });
    },
  });
};

// Helper hook: check if a specific lead is saved for a retailer
export const useIsLeadFavorite = (leadId: string, retailer: string) => {
  const compositeKey = makeLeadFavoriteKey(retailer, leadId);
  const { data: ids = [] } = useLeadFavoriteIDs(retailer);
  return ids.includes(compositeKey);
};
